export function normalizeCode(value) {
  if (value == null) return ''
  return String(value).trim().replace(/\s+/g, ' ').toUpperCase().slice(0, 64)
}

export function addInquiryCode(codes, value) {
  const current = Array.isArray(codes) ? codes.map(normalizeCode).filter(Boolean) : []
  const code = normalizeCode(value)
  if (!code || current.includes(code)) return current
  return [...current, code]
}

export function removeInquiryCode(codes, value) {
  const code = normalizeCode(value)
  return (Array.isArray(codes) ? codes : []).map(normalizeCode).filter((item) => item && item !== code)
}

export function validateInquiry({ email = '', message = '', codes = [] } = {}) {
  const errors = {}
  const normalizedEmail = String(email).trim()
  const normalizedMessage = String(message).trim()
  const normalizedCodes = Array.isArray(codes) ? codes.map(normalizeCode).filter(Boolean) : []

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
    errors.email = 'Enter a valid email address.'
  }
  if (normalizedCodes.length === 0 && normalizedMessage.length < 10) {
    errors.context = 'Add a style or describe the project.'
  }

  return { valid: Object.keys(errors).length === 0, errors }
}

function initInquiry() {
  document.documentElement.classList.remove('no-js')
  document.documentElement.classList.add('js')

  if (document.querySelector('[data-official-inquiry]')) return

  const layer = document.querySelector('[data-inquiry-layer]')
  const drawer = document.querySelector('[data-inquiry-drawer]')
  if (!layer || !drawer) return

  const quickContact = document.querySelector('[data-quick-contact]')
  const quickToggle = quickContact?.querySelector('[data-quick-contact-toggle]')
  const quickPanel = quickContact?.querySelector('[data-quick-contact-panel]')
  const quickForm = quickContact?.querySelector('[data-quick-contact-form]')
  const quickEmail = quickContact?.querySelector('[data-quick-contact-email]')
  const quickFeedback = quickContact?.querySelector('[data-quick-contact-feedback]')
  const countNodes = document.querySelectorAll('[data-inquiry-count]')
  const codeList = drawer.querySelector('[data-inquiry-code-list]')
  const codesInput = drawer.querySelector('[data-inquiry-codes-input]')
  const form = drawer.querySelector('form')
  const feedback = drawer.querySelector('[data-inquiry-feedback]')
  const emailInput = drawer.querySelector('[data-inquiry-email]')
  const messageInput = drawer.querySelector('[data-inquiry-message]')
  const demoSubmit = drawer.querySelector('[data-inquiry-demo-submit]')
  let codes = []
  let returnFocus = null

  const setQuickContactOpen = (isOpen, restoreFocus = false) => {
    if (!quickPanel || !quickToggle) return
    quickPanel.hidden = !isOpen
    quickToggle.setAttribute('aria-expanded', String(isOpen))
    quickContact?.classList.toggle('is-open', isOpen)
    if (isOpen) {
      quickFeedback?.replaceChildren()
      quickEmail?.setAttribute('aria-invalid', 'false')
      quickEmail?.focus()
    } else if (restoreFocus) {
      quickToggle.focus()
    }
  }

  const render = () => {
    countNodes.forEach((node) => {
      node.textContent = String(codes.length)
    })
    if (codesInput) codesInput.value = codes.join(', ')
    if (!codeList) return

    codeList.closest('.inquiry-selection').hidden = codes.length === 0
    codeList.replaceChildren()
    if (codes.length === 0) {
      const empty = document.createElement('li')
      empty.className = 'inquiry-code-list__empty'
      empty.textContent = 'No style selected yet. Add one from a product or collection page.'
      codeList.append(empty)
      return
    }

    codes.forEach((code) => {
      const item = document.createElement('li')
      const label = document.createElement('span')
      const remove = document.createElement('button')
      label.textContent = code
      remove.type = 'button'
      remove.dataset.inquiryRemove = code
      remove.setAttribute('aria-label', `Remove style ${code}`)
      remove.textContent = 'Remove'
      item.append(label, remove)
      codeList.append(item)
    })
  }

  const open = (trigger, seedEmail = '') => {
    returnFocus = trigger || document.activeElement
    setQuickContactOpen(false)
    layer.hidden = false
    drawer.hidden = false
    document.body.classList.add('inquiry-open')
    if (seedEmail && emailInput) emailInput.value = seedEmail
    drawer.querySelector('[data-inquiry-close]')?.focus()
  }

  const close = () => {
    layer.hidden = true
    drawer.hidden = true
    document.body.classList.remove('inquiry-open')
    if (returnFocus instanceof HTMLElement) returnFocus.focus()
  }

  quickForm?.addEventListener('submit', (event) => {
    event.preventDefault()
    const email = String(quickEmail?.value || '').trim()
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    quickEmail?.setAttribute('aria-invalid', String(!valid))
    if (!valid) {
      if (quickFeedback) quickFeedback.textContent = 'Enter a valid email address.'
      quickEmail?.focus()
      return
    }
    if (quickFeedback) quickFeedback.textContent = ''
    open(quickToggle, email)
  })

  document.addEventListener('click', (event) => {
    const quickToggleTrigger = event.target.closest('[data-quick-contact-toggle]')
    if (quickToggleTrigger) {
      setQuickContactOpen(quickToggleTrigger.getAttribute('aria-expanded') !== 'true')
      return
    }
    if (event.target.closest('[data-quick-contact-close]')) {
      setQuickContactOpen(false, true)
      return
    }

    const addTrigger = event.target.closest('[data-inquiry-add]')
    if (addTrigger) {
      event.preventDefault()
      codes = addInquiryCode(codes, addTrigger.dataset.productCode)
      render()
      open(addTrigger)
      return
    }

    const removeTrigger = event.target.closest('[data-inquiry-remove]')
    if (removeTrigger) {
      codes = removeInquiryCode(codes, removeTrigger.dataset.inquiryRemove)
      render()
      return
    }

    const openTrigger = event.target.closest('[data-inquiry-open]')
    if (openTrigger) open(openTrigger)
    if (event.target.closest('[data-inquiry-close]')) close()
    if (quickPanel && !quickPanel.hidden && !event.target.closest('[data-quick-contact]')) setQuickContactOpen(false)
  })

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !layer.hidden) {
      close()
      return
    }
    if (event.key === 'Escape' && quickPanel && !quickPanel.hidden) {
      setQuickContactOpen(false, true)
      return
    }
    if (event.key !== 'Tab' || layer.hidden) return

    const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')]
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable.at(-1)
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  })

  form?.addEventListener('submit', (event) => {
    const result = validateInquiry({
      email: emailInput?.value,
      message: messageInput?.value,
      codes,
    })

    if (!result.valid || form.dataset.liveDelivery !== 'true') event.preventDefault()
    emailInput?.setAttribute('aria-invalid', String(Boolean(result.errors.email)))

    if (!feedback) return
    if (!result.valid) {
      feedback.textContent = Object.values(result.errors).join(' ')
      feedback.dataset.state = 'error'
      return
    }

    if (form.dataset.liveDelivery !== 'true') {
      feedback.textContent = 'Demo inquiry prepared. Live delivery is currently disabled in Theme Settings.'
      feedback.dataset.state = 'success'
    }
  })

  render()
  if (demoSubmit) demoSubmit.disabled = false
  if (feedback?.dataset.serverState) {
    open()
    feedback.focus()
  }
}

function initMediaFallbacks(root = document) {
  root.querySelectorAll('[data-media-image]').forEach((image) => {
    const showFallback = () => {
      const frame = image.closest('[data-media-frame]')
      if (!frame || frame.classList.contains('media-frame--error')) return

      const fallback = document.createElement('div')
      const code = document.createElement('span')
      const note = document.createElement('small')
      fallback.className = 'media-frame__error'
      fallback.setAttribute('role', 'img')
      fallback.setAttribute('aria-label', `${image.alt || 'Image'} is unavailable`)
      code.textContent = frame.dataset.fallbackCode || 'IMAGE'
      note.textContent = 'Image temporarily unavailable'
      fallback.append(code, note)
      frame.classList.add('media-frame--error')
      frame.append(fallback)
    }

    image.addEventListener('error', showFallback, { once: true })
    if (image.complete && image.naturalWidth === 0) showFallback()
  })
}

function initProductModels() {
  const modelFrames = [...document.querySelectorAll('[data-product-model]')]
  if (modelFrames.length === 0) return

  const showModelError = (message) => {
    modelFrames.forEach((frame) => {
      frame.dataset.modelState = 'error'
      if (frame.querySelector('[data-model-status]')) return
      const status = document.createElement('p')
      status.className = 'product-native-media__status'
      status.dataset.modelStatus = ''
      status.setAttribute('role', 'status')
      status.textContent = message
      frame.append(status)
    })
  }

  if (!window.Shopify?.loadFeatures) {
    showModelError('Interactive 3D controls are temporarily unavailable.')
    return
  }

  window.Shopify.loadFeatures([
    {
      name: 'model-viewer-ui',
      version: '1.0',
      onLoad: (errors) => {
        if (errors || !window.Shopify.ModelViewerUI) {
          showModelError('Interactive 3D controls are temporarily unavailable.')
          return
        }

        modelFrames.forEach((frame) => {
          const modelViewer = frame.querySelector('model-viewer')
          if (!modelViewer) return
          try {
            frame.modelViewerUI = new window.Shopify.ModelViewerUI(modelViewer)
            frame.dataset.modelState = 'ready'
          } catch {
            frame.dataset.modelState = 'error'
          }
        })
      },
    },
    {
      name: 'shopify-xr',
      version: '1.0',
      onLoad: (errors) => {
        if (errors || !window.ShopifyXR) return
        document.querySelectorAll('[data-product-model-json]').forEach((modelJson) => {
          try {
            window.ShopifyXR.addModels(JSON.parse(modelJson.textContent))
          } catch {
            return
          }
        })
        window.ShopifyXR.setupXRElements()
      },
    },
  ])
}

function initProductGallery() {
  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')]
    const previous = gallery.querySelector('[data-gallery-previous]')
    const next = gallery.querySelector('[data-gallery-next]')
    const status = gallery.querySelector('[data-gallery-status]')
    const thumbnailRail = gallery.querySelector('[data-product-gallery-thumbnails]')
    const original = gallery.querySelector('[data-gallery-original]')
    if (slides.length === 0) return

    let activeIndex = 0
    let pointerStart = null

    const thumbnails = slides.map((slide, index) => {
      const button = document.createElement('button')
      const image = slide.querySelector('img')
      const video = slide.querySelector('video')
      const model = slide.querySelector('model-viewer')
      const source = image?.currentSrc || image?.src || video?.poster || model?.poster
      const label = slide.dataset.galleryLabel || `Media ${index + 1}`

      button.type = 'button'
      button.className = 'product-gallery__thumbnail'
      button.dataset.galleryThumbnail = String(index)
      button.setAttribute('aria-label', `Show ${label}`)

      if (source) {
        const thumb = document.createElement('img')
        thumb.src = source
        thumb.alt = ''
        thumb.loading = 'lazy'
        button.append(thumb)
      } else {
        const fallback = document.createElement('span')
        fallback.textContent = String(index + 1).padStart(2, '0')
        button.append(fallback)
      }

      const caption = document.createElement('small')
      const shortLabel = label
      caption.textContent = `${String(index + 1).padStart(2, '0')} / ${shortLabel}`
      button.append(caption)
      button.addEventListener('click', () => select(index, true))
      return button
    })

    thumbnailRail?.replaceChildren(...thumbnails)

    const select = (requestedIndex, userInitiated = false) => {
      activeIndex = (requestedIndex + slides.length) % slides.length
      gallery.dataset.galleryActiveIndex = String(activeIndex)
      gallery.dispatchEvent(new Event('gallery:change'))

      slides.forEach((slide, index) => {
        const selected = index === activeIndex
        slide.hidden = !selected
        slide.setAttribute('aria-hidden', String(!selected))
        if (!selected) slide.querySelector('video')?.pause()
      })

      thumbnails.forEach((thumbnail, index) => {
        const selected = index === activeIndex
        thumbnail.classList.toggle('is-selected', selected)
        thumbnail.setAttribute('aria-pressed', String(selected))
      })

      const label = slides[activeIndex].dataset.galleryLabel || `Media ${activeIndex + 1}`
      gallery.dataset.galleryActiveLabel = label
      if (original) {
        const image = slides[activeIndex].querySelector('img')
        original.hidden = !image?.src
        if (image?.src) {
          original.href = image.src
          original.setAttribute('aria-label', `View full ${label} image (opens in a new tab)`)
        } else {
          original.removeAttribute('href')
        }
      }
      if (status) status.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')} — ${label}`
      const selectedThumbnail = thumbnails[activeIndex]
      if (userInitiated && thumbnailRail && selectedThumbnail && thumbnailRail.scrollWidth > thumbnailRail.clientWidth) {
        const left = selectedThumbnail.offsetLeft - ((thumbnailRail.clientWidth - selectedThumbnail.clientWidth) / 2)
        thumbnailRail.scrollTo({ left: Math.max(0, left), behavior: 'smooth' })
      }
    }

    previous?.addEventListener('click', () => select(activeIndex - 1, true))
    gallery.addEventListener('gallery:step', (event) => select(activeIndex + event.detail.step, true))
    next?.addEventListener('click', () => select(activeIndex + 1, true))
    previous?.toggleAttribute('disabled', slides.length < 2)
    next?.toggleAttribute('disabled', slides.length < 2)

    gallery.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      event.preventDefault()
      select(activeIndex + (event.key === 'ArrowRight' ? 1 : -1), true)
    })

    gallery.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return
      pointerStart = event.clientX
    })

    gallery.addEventListener('pointerup', (event) => {
      if (pointerStart == null) return
      const distance = event.clientX - pointerStart
      pointerStart = null
      if (Math.abs(distance) < 48) return
      select(activeIndex + (distance < 0 ? 1 : -1), true)
    })

    gallery.addEventListener('pointercancel', () => {
      pointerStart = null
    })

    select(0)
  })
}

function initConstructionHotspots() {
  document.querySelectorAll('[data-construction-hotspots]').forEach((group) => {
    const hotspots = [...group.querySelectorAll('[data-construction-hotspot]')]
    const stackedLayout = window.matchMedia('(max-width: 760px)')

    const closeAll = (except = null) => {
      hotspots.forEach((hotspot) => {
        if (hotspot === except) return
        hotspot.setAttribute('aria-expanded', 'false')
        const popover = document.getElementById(hotspot.getAttribute('aria-controls'))
        if (popover) popover.hidden = true
      })
    }

    const open = (hotspot) => {
      closeAll(hotspot)
      hotspot.setAttribute('aria-expanded', 'true')
      const popover = document.getElementById(hotspot.getAttribute('aria-controls'))
      if (popover) popover.hidden = false
    }

    group.addEventListener('click', (event) => {
      if (stackedLayout.matches) return
      const hotspot = event.target.closest('[data-construction-hotspot]')
      if (hotspot) open(hotspot)
    })
    group.addEventListener('focusin', (event) => {
      if (stackedLayout.matches) return
      const hotspot = event.target.closest('[data-construction-hotspot]')
      if (hotspot) open(hotspot)
    })
    group.addEventListener('pointerover', (event) => {
      if (stackedLayout.matches) return
      const hotspot = event.target.closest('[data-construction-hotspot]')
      if (hotspot && window.matchMedia('(hover: hover)').matches) open(hotspot)
    })
    group.addEventListener('pointerleave', () => {
      if (!group.contains(document.activeElement)) closeAll()
    })
    group.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return
      closeAll()
      event.target.closest('[data-construction-hotspot]')?.focus()
    })
    document.addEventListener('click', (event) => {
      if (!stackedLayout.matches && !group.contains(event.target)) closeAll()
    })

    const syncLayout = () => {
      if (!stackedLayout.matches) {
        closeAll()
        return
      }
      hotspots.forEach((hotspot) => {
        hotspot.setAttribute('aria-expanded', 'true')
        const popover = document.getElementById(hotspot.getAttribute('aria-controls'))
        if (popover) popover.hidden = false
      })
    }
    stackedLayout.addEventListener?.('change', syncLayout)
    syncLayout()
  })
}

function initAutomatedStory() {
  const stories = [...document.querySelectorAll('[data-automated-story]')]
  if (stories.length === 0) return

  const mobileQuery = window.matchMedia('(max-width: 900px)')
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

  stories.forEach((story) => {
    const panels = [...story.querySelectorAll('[data-story-panel]')]
    const triggers = [...story.querySelectorAll('[data-story-trigger]')]
    const status = story.querySelector('[data-story-status]')
    let observer
    let stopScrollMotion = () => {}

    if (panels.length === 0) return

    const stageStatus = (index) => {
      const current = String(index + 1).padStart(2, '0')
      const total = String(panels.length).padStart(2, '0')
      const title = panels[index]?.dataset.storyTitle || ''
      return `${current} / ${total}${title ? ` — ${title}` : ''}`
    }

    const activate = (index) => {
      const safeIndex = Math.max(0, Math.min(index, panels.length - 1))
      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === safeIndex
        panel.classList.toggle('is-active', active)
        panel.setAttribute('aria-hidden', String(!active))
      })
      triggers.forEach((trigger, triggerIndex) => trigger.classList.toggle('is-active', triggerIndex === safeIndex))
      story.style.setProperty('--story-progress', panels.length > 1 ? String(safeIndex / (panels.length - 1)) : '1')
      if (status) status.textContent = stageStatus(safeIndex)
    }

    const exposeAll = () => {
      story.classList.add('is-static')
      story.classList.remove('is-mobile')
      panels.forEach((panel) => {
        panel.classList.add('is-revealed')
        panel.classList.remove('is-active')
        panel.removeAttribute('aria-hidden')
      })
    }

    // Native scrolling keeps every mobile chapter in normal document flow.
    const startMobileMotion = () => {
      const clamp = (value) => Math.max(0, Math.min(1, value))
      const captions = panels.map((panel) => panel.querySelector('.automated-story__caption'))
      const revealed = panels.map(() => 0)
      let frame = 0
      let current = -1
      let disposed = false
      story.classList.add('has-scroll-motion')

      const update = () => {
        frame = 0
        if (disposed || !story.isConnected || document.hidden) return
        const height = window.innerHeight
        const rootBox = story.getBoundingClientRect()
        const readingLine = height * 0.46
        const reading = rootBox.top < readingLine && rootBox.bottom > readingLine
        if (rootBox.top >= height || rootBox.bottom <= 0) {
          story.classList.remove('is-reading')
          return
        }
        // Read geometry together before setting any animation properties.
        const boxes = panels.map((panel) => panel.getBoundingClientRect())
        const captionTops = captions.map((caption) => caption?.getBoundingClientRect().top ?? height)
        let active = boxes.findIndex((box) => box.bottom > readingLine)
        if (active < 0) active = panels.length - 1
        const progress = clamp((active + clamp((readingLine - boxes[active].top) / boxes[active].height)) / panels.length)
        story.classList.toggle('is-reading', reading)
        story.style.setProperty('--story-progress', progress.toFixed(4))
        if (reading && current !== active) {
          current = active
          if (status) status.textContent = stageStatus(active)
        }
        boxes.forEach((box, index) => {
          if (box.bottom <= 0 || box.top >= height) return
          const travel = clamp((height - box.top) / (height + box.height))
          const reveal = clamp((height * 0.96 - captionTops[index]) / Math.min(160, height * 0.22))
          revealed[index] = Math.max(revealed[index], reveal)
          const value = revealed[index]
          panels[index].style.setProperty('--story-image-shift', ((0.5 - travel) * Math.min(16, box.height * 0.025)).toFixed(2) + 'px')
          panels[index].style.setProperty('--story-caption-offset', ((1 - value) * 14).toFixed(2) + 'px')
          panels[index].style.setProperty('--story-caption-clip', ((1 - value) * 65).toFixed(2) + '%')
          panels[index].style.setProperty('--story-copy-opacity', (0.35 + 0.65 * value).toFixed(3))
          panels[index].style.setProperty('--story-chapter-progress', clamp((height - box.top) / (height + box.height * 0.25)).toFixed(4))
        })
      }
      const schedule = () => {
        if (!frame && !disposed) frame = window.requestAnimationFrame(update)
      }
      window.addEventListener('scroll', schedule, { passive: true })
      window.addEventListener('resize', schedule, { passive: true })
      document.addEventListener('visibilitychange', schedule)
      update()
      return () => {
        disposed = true
        window.cancelAnimationFrame(frame)
        window.removeEventListener('scroll', schedule)
        window.removeEventListener('resize', schedule)
        document.removeEventListener('visibilitychange', schedule)
        story.classList.remove('has-scroll-motion', 'is-reading')
        panels.forEach((panel) => {
          for (const property of ['--story-image-shift', '--story-caption-offset', '--story-caption-clip', '--story-copy-opacity', '--story-chapter-progress']) {
            panel.style.removeProperty(property)
          }
        })
      }
    }

    const configure = () => {
      observer?.disconnect()
      stopScrollMotion()
      story.classList.remove('is-static', 'is-mobile')
      panels.forEach((panel) => panel.classList.remove('is-revealed'))

      const stickyEnabled = story.dataset.storySticky !== 'false'
      if (motionQuery.matches || !stickyEnabled || !('IntersectionObserver' in window)) {
        exposeAll()
        return
      }

      if (mobileQuery.matches) {
        story.classList.add('is-mobile')
        panels.forEach((panel) => {
          panel.classList.remove('is-active')
          panel.removeAttribute('aria-hidden')
        })
        observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return
            entry.target.classList.add('is-revealed')
            observer.unobserve(entry.target)
          })
        }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 })
        panels.forEach((panel) => observer.observe(panel))
        stopScrollMotion = startMobileMotion()
        return
      }

      activate(0)
      observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => Number(a.target.dataset.storyIndex) - Number(b.target.dataset.storyIndex))
        if (visible.length === 0) return
        activate(Number(visible.at(-1).target.dataset.storyIndex))
      }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 })
      triggers.forEach((trigger) => observer.observe(trigger))
    }

    mobileQuery.addEventListener?.('change', configure)
    motionQuery.addEventListener?.('change', configure)
    configure()
  })
}

function initScrollReveals() {
  const targets = [...document.querySelectorAll('[data-reveal]')]
  if (targets.length === 0) return

  const reveal = (target) => target.classList.add('is-revealed')
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
    targets.forEach(reveal)
    return
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return
      reveal(entry.target)
      observer.unobserve(entry.target)
    })
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 })

  targets.forEach((target) => observer.observe(target))
}

if (typeof document !== 'undefined') {
  document.addEventListener('collection:updated', (event) => initMediaFallbacks(event.target))
  initInquiry()
  initMediaFallbacks()
  initProductGallery()
  initProductModels()
  initConstructionHotspots()
  initAutomatedStory()
  initScrollReveals()
}
